/**
 * incomingCallState.ts — PHONE-A1 (V-01/V-02, 2026-09-02). The ring's state
 * machine, extracted from TwilioContext so it can be tested without the Twilio
 * SDK, a WebSocket, or a browser.
 *
 * ─── WHAT WAS WRONG ───────────────────────────────────────────────────────
 *
 * V-01 THE SOCKET WAS THE ONLY RING TRIGGER. The banner and the ring tone were
 * driven exclusively by the `phone.incoming_call` WebSocket event. The Twilio
 * Device's own 'incoming' event — the one that arrives over Twilio's signalling
 * channel, the one that means a call is ACTUALLY on this browser — only parked
 * the Call object in a ref and wired listeners. It rendered nothing and made no
 * sound. So a dropped socket (an expired session, a Railway redeploy, a laptop
 * that had just woken) produced a completely silent phone while Twilio sat
 * there ringing an invitation nobody could see. The signalling channel that
 * carries the call must be the thing that announces it; the socket's job is to
 * make the announcement RICHER (the caller's name, which Twilio does not know).
 *
 * V-02 ACCEPT COULD SUCCEED WITH NO CALL TO ACCEPT. `acceptIncomingCall`
 * guarded `call.accept()` behind `if (call)` — and then unconditionally set
 * callState to 'open', opened the dialer and cleared the banner. The server
 * publishes the WS event BEFORE it returns TwiML, so the banner reliably beats
 * the SDK invite: answer fast enough and you got a live-looking call with
 * nobody on it, while the real leg rang on and went to voicemail. Fixing the
 * race by hiding the buttons until the invite lands would be worse — it would
 * make the phone feel broken for the first second of every call. So the click
 * is REMEMBERED and applied the moment the invite arrives.
 *
 * ─── THE SID PROBLEM ──────────────────────────────────────────────────────
 *
 * The two channels do not agree on the call's identity. Twilio's
 * <Dial><Client> creates a CHILD leg, and the SDK fires 'incoming' with that
 * CHILD sid in `call.parameters.CallSid`. The WS event carries the PARENT sid,
 * which is what `comm_call_logs` was keyed on at routing time — so the by-sid
 * lookup only works with the parent. Both are therefore kept on the banner, and
 * matching falls back to phone numbers within a ring-window when the sids
 * differ (which is the normal case, not the exception).
 *
 * ─── BULK-1 WP-Calls (2026-10-26): "client:user_1" ──────────────────────
 *
 * The desktop card showed "client:user_1" under the caller and lost their
 * name. On a <Dial><Client> invite the SDK's `To` is not a phone number at
 * all — it is the BROWSER's identity, `client:user_<id>` — while the WS event
 * carries the BSG line. The number match compared the two ("1" against the
 * line's ten digits), decided they were different calls, and the raw invite
 * REPLACED the named banner a split second after it appeared. Two fixes:
 *   - a `client:` identity is never a phone number here: it reads as absent
 *     (a missing To was already "not a mismatch");
 *   - the ring TwiML now hands the browser the PARENT sid as a custom
 *     parameter (`twilioService.generateInboundRingTwiml`), so the two
 *     announcements match by sid whenever it is there — an anonymous caller,
 *     whose From has no digits to match on, included.
 */

export type RingIntent = 'accept' | 'decline' | 'voicemail';

/** Where a banner's fields came from. Diagnostic, and it drives the merge. */
export type RingSource = 'device' | 'ws';

export interface IncomingCallBanner {
  /**
   * The best sid to LOOK THIS CALL UP by — the PARENT sid once the WS event has
   * supplied it, otherwise whatever the SDK gave us. `/communications/calls/by-sid`
   * only resolves the parent.
   */
  callSid: string;
  /** The sid from the WS event: the parent leg, the comm_call_logs key. */
  parentCallSid?: string | null;
  /** The sid from `call.parameters.CallSid`: the CHILD leg the browser answers. */
  sdkCallSid?: string | null;
  fromNumber: string;
  /** CRM-resolved caller name. Twilio does not know it, so this is WS-only. */
  fromName?: string | null;
  toNumber: string;
  /** WS-only: which assigned user this ring was published to. */
  toUserId?: number | null;
  /** ISO instant the ring gives up. Device-born banners assume the ring window. */
  ringExpiresAt: string;
  /** Which channel announced it first. */
  announcedBy: RingSource;
  /** True once the WS event has enriched a device-born banner (or vice versa). */
  enriched: boolean;
  /**
   * Set when a click has been REMEMBERED because the SDK invite has not arrived
   * yet (V-02). The banner stays up showing this label instead of the buttons,
   * and the ring tone stops. Null the rest of the time.
   *
   * It is a label rather than a boolean so the banner can say which click it is
   * holding: a user who pressed Decline and sees "Connecting…" would reasonably
   * think they had just answered a call they meant to refuse.
   */
  pendingLabel: string | null;
}

export interface IncomingCallState {
  banner: IncomingCallBanner | null;
  /** A click that arrived before the SDK Call object did. */
  pendingIntent: RingIntent | null;
}

export const EMPTY_RING_STATE: IncomingCallState = { banner: null, pendingIntent: null };

/** How long a device-born banner rings before it dismisses itself. Mirrors
 *  RING_TIMEOUT_SECONDS in backend/src/routes/communications.ts. */
export const DEFAULT_RING_MS = 25_000;

/**
 * How far apart two announcements of the same call may be and still be matched
 * by phone number. Generous on purpose: the two channels are independent, and
 * the cost of a false NEGATIVE (two stacked banners for one call) is far worse
 * for the user than the cost of a false positive (one banner enriched with a
 * name from a near-simultaneous second call from the same number, which would
 * require the same person to call the same line twice inside ten seconds).
 */
export const SID_MATCH_WINDOW_MS = 10_000;

const PENDING_LABELS: Record<RingIntent, string> = {
  accept: 'Connecting…',
  decline: 'Declining…',
  voicemail: 'Sending to voicemail…',
};

export interface WsIncomingCallEvent {
  callSid: string;
  fromNumber: string;
  fromName?: string | null;
  toNumber: string;
  toUserId: number;
  ringExpiresAt: string;
}

/** The subset of `call.parameters` the SDK guarantees on an inbound invite. */
export interface DeviceCallParams {
  From?: string;
  To?: string;
  CallSid?: string;
  /**
   * BULK-1 WP-Calls — the PARENT leg's sid, read from `call.customParameters`
   * (the ring TwiML's `<Parameter name="parentCallSid">`). Absent on a ring
   * from a server that predates it.
   */
  ParentCallSid?: string;
}

/**
 * A number off the Twilio invite, or '' when it is a browser identity
 * (`client:user_1`) — that names the app user being rung, never a phone.
 */
export function deviceNumber(value?: string | null): string {
  const s = String(value ?? '').trim();
  return /^client:/i.test(s) ? '' : s;
}

export type IncomingCallAction =
  /** The Twilio Device fired 'incoming'. The call is REALLY here. */
  | { type: 'device_incoming'; params: DeviceCallParams; nowMs: number; ringMs?: number }
  /** The server's WebSocket announcement arrived. */
  | { type: 'ws_incoming'; event: WsIncomingCallEvent; nowMs: number }
  /** The user clicked Accept / Decline / Voicemail. */
  | { type: 'user_intent'; intent: RingIntent; hasCallObject: boolean }
  /** The ring window elapsed with nothing answered. */
  | { type: 'ring_timeout' }
  /** Resolved, cancelled, or torn down for any other reason. */
  | { type: 'clear' };

export interface IncomingCallResult {
  state: IncomingCallState;
  /**
   * Non-null on EXACTLY the transition where a remembered click becomes
   * actionable — i.e. the SDK invite arrived while an intent was pending. The
   * context performs `call.accept()` / `call.reject()` on this signal, and
   * never on the reducer's state alone, so the effect fires once and only once.
   */
  applyIntent: RingIntent | null;
  /**
   * Non-null when the user should be told something. Today only the
   * "you answered but the invite never came" case, which would otherwise be a
   * banner that silently vanished after a click.
   */
  toast: string | null;
}

const ok = (state: IncomingCallState): IncomingCallResult => ({ state, applyIntent: null, toast: null });

function digits(value?: string | null): string {
  return String(value || '').replace(/\D/g, '').slice(-10);
}

/**
 * Are these two announcements the same call?
 *
 * Sid equality first — but the SDK's child sid and the WS event's parent sid
 * are DIFFERENT strings for the same call, so a sid mismatch proves nothing.
 * Falls back to "same two phone numbers, within the ring window", comparing the
 * last ten digits because the two channels format numbers differently (the WS
 * event goes through the backend's normalizePhone; the SDK reports whatever
 * Twilio sent).
 */
export function isSameCall(
  banner: IncomingCallBanner,
  candidate: { callSid?: string | null; parentCallSid?: string | null; fromNumber?: string | null; toNumber?: string | null },
  nowMs: number,
  windowMs: number = SID_MATCH_WINDOW_MS,
): boolean {
  for (const sid of [candidate.callSid, candidate.parentCallSid]) {
    if (sid && (sid === banner.parentCallSid || sid === banner.sdkCallSid || sid === banner.callSid)) return true;
  }

  // BULK-1 WP-Calls — a browser identity ("client:user_1") is not a number:
  // it reads as absent, never as a different To.
  const from = digits(deviceNumber(candidate.fromNumber));
  const to = digits(deviceNumber(candidate.toNumber));
  if (!from || from !== digits(banner.fromNumber)) return false;
  // A missing To on either side is not a mismatch — only a present, DIFFERENT
  // one is. The SDK omits it on some edges.
  const bannerTo = digits(deviceNumber(banner.toNumber));
  if (to && bannerTo && to !== bannerTo) return false;

  const expiresMs = Date.parse(banner.ringExpiresAt);
  const stillRinging = Number.isFinite(expiresMs) ? nowMs <= expiresMs + windowMs : true;
  return stillRinging;
}

export function incomingCallReducer(
  state: IncomingCallState,
  action: IncomingCallAction,
): IncomingCallResult {
  switch (action.type) {
    case 'device_incoming': {
      const { params, nowMs } = action;
      const ringMs = action.ringMs ?? DEFAULT_RING_MS;
      const sdkSid = params.CallSid || null;
      const parentSid = params.ParentCallSid || null;
      // BULK-1 WP-Calls — the invite's To is the browser ("client:user_1"):
      // never shown, never matched as a phone number.
      const fromNumber = deviceNumber(params.From);
      const toNumber = deviceNumber(params.To);

      // The socket got here first — today's normal order, because the server
      // publishes before it returns TwiML. Keep that banner exactly as it is
      // (it has the caller's NAME, which this channel cannot know) and only
      // record the child sid.
      if (state.banner && isSameCall(state.banner, { callSid: sdkSid, parentCallSid: parentSid, fromNumber, toNumber }, nowMs)) {
        const banner: IncomingCallBanner = {
          ...state.banner,
          sdkCallSid: sdkSid ?? state.banner.sdkCallSid ?? null,
          // A banner that had no parent sid yet (two device invites) takes it.
          ...(parentSid && !state.banner.parentCallSid ? { callSid: parentSid, parentCallSid: parentSid } : {}),
          enriched: true,
        };
        // A click that landed in the gap becomes actionable right now. The
        // context performs it on `applyIntent` and then clears, so the label
        // comes off here.
        if (state.pendingIntent) {
          return {
            state: { banner: { ...banner, pendingLabel: null }, pendingIntent: null },
            applyIntent: state.pendingIntent,
            toast: null,
          };
        }
        return ok({ banner, pendingIntent: null });
      }

      // Nothing on screen, or a DIFFERENT call: the Device announces it itself.
      // This is V-01's whole fix — before, this branch rendered nothing at all
      // and the phone was silent whenever the socket was down.
      const banner: IncomingCallBanner = {
        // The parent sid, when the ring TwiML sent it, is the one the by-sid lookup resolves.
        callSid: parentSid || sdkSid || '',
        parentCallSid: parentSid,
        sdkCallSid: sdkSid,
        fromNumber,
        // Twilio has no CRM; the name arrives (if ever) with the WS event.
        fromName: null,
        toNumber,
        toUserId: null,
        ringExpiresAt: new Date(nowMs + ringMs).toISOString(),
        announcedBy: 'device',
        enriched: false,
        pendingLabel: null,
      };
      // An intent pending against a DIFFERENT call is stale — it belonged to a
      // ring that never produced an invite. Dropping it here is what stops a
      // stale Accept from answering the next caller.
      return { state: { banner, pendingIntent: null }, applyIntent: null, toast: null };
    }

    case 'ws_incoming': {
      const { event, nowMs } = action;
      const incoming: IncomingCallBanner = {
        callSid: event.callSid,
        parentCallSid: event.callSid,
        sdkCallSid: null,
        fromNumber: event.fromNumber,
        fromName: event.fromName ?? null,
        toNumber: event.toNumber,
        toUserId: event.toUserId,
        ringExpiresAt: event.ringExpiresAt,
        announcedBy: 'ws',
        enriched: false,
        pendingLabel: null,
      };

      // The Device announced it first (the socket was slow, or reconnecting).
      // MERGE rather than replace: replacing would throw away `connecting`, and
      // would restart the ring on a call the user has already answered.
      if (state.banner && isSameCall(state.banner, { callSid: event.callSid, fromNumber: event.fromNumber, toNumber: event.toNumber }, nowMs)) {
        return ok({
          banner: {
            ...state.banner,
            // The parent sid is strictly better for the by-sid lookup than the
            // child sid the Device gave us, so it becomes `callSid`.
            callSid: event.callSid,
            parentCallSid: event.callSid,
            fromName: event.fromName ?? state.banner.fromName ?? null,
            toUserId: event.toUserId ?? state.banner.toUserId ?? null,
            ringExpiresAt: event.ringExpiresAt || state.banner.ringExpiresAt,
            // BULK-1 WP-Calls — the server's numbers win: its To is the BSG
            // line (the invite's is the browser), and both are normalised.
            fromNumber: event.fromNumber || state.banner.fromNumber,
            toNumber: deviceNumber(event.toNumber) || deviceNumber(state.banner.toNumber),
            enriched: true,
            // `pendingLabel` is carried through by the spread on purpose: a WS
            // event must never un-answer a call the user has already clicked.
          },
          pendingIntent: state.pendingIntent,
        });
      }

      // Nothing on screen, or a different call — today's behaviour, unchanged.
      return ok({ banner: incoming, pendingIntent: null });
    }

    case 'user_intent': {
      if (!state.banner) return ok(state);

      // The SDK Call object is here: act now, and let the context clear.
      if (action.hasCallObject) {
        return { state, applyIntent: action.intent, toast: null };
      }

      // V-02: no Call object yet. REMEMBER the click instead of pretending it
      // worked. The banner stays up with a label so the click is visibly
      // registered, and the ring stops — a phone that keeps ringing after you
      // press Answer is the clearest possible signal that it is broken.
      return ok({
        banner: { ...state.banner, pendingLabel: PENDING_LABELS[action.intent] },
        pendingIntent: action.intent,
      });
    }

    case 'ring_timeout': {
      // A pending intent at timeout means the user DID answer and the invite
      // never arrived. That must be said out loud — otherwise the banner just
      // disappears after a click and the user cannot tell whether they are on a
      // call, whether it failed, or whether they mis-clicked.
      const toast = state.pendingIntent === 'accept'
        ? 'The call ended before it could be answered.'
        : null;
      return { state: EMPTY_RING_STATE, applyIntent: null, toast };
    }

    case 'clear':
    default:
      return ok(EMPTY_RING_STATE);
  }
}
